import React from 'react';
import '../css/animate.css';
import '../css/barvy.css';
import '../css/style.css';
import { NavLink } from 'react-router-dom';
import { FaInstagram } from "react-icons/fa6";
import LanguageSwitcher from './LanguageSwitcher';
import { getLocalizedText } from '../utils/textLocalization';
import {
  EditableContent,
  useEditry,
  usePreloadPublicData,
  usePublicContentList,
  getModelEntryData,
} from '@lazylabs-dev/editry-react';
import { isEditryConfigured } from '../utils/editryConfig';
import { getCurrentLanguage } from '../utils/languageSwitcher';
import { useState } from 'react';
import { useEffect } from 'react';
import { useMemo } from 'react';

const LOCALE_TO_CONTENT_LANGUAGE = {
  'cs-CZ': 'cs',
  'sk-SK': 'sk',
  'pl-PL': 'pl',
  'hu-HU': 'hu',
};

const getPreferredLanguages = (locale) => {
  const preferred = LOCALE_TO_CONTENT_LANGUAGE[locale] || 'cs';
  return preferred === 'cs' ? ['cs', 'default'] : [preferred, 'cs', 'default'];
};

const normalizeContentLanguage = (language) => {
  if (typeof language !== 'string') {
    return 'default';
  }

  const normalized = language.trim().toLowerCase();
  return normalized || 'default';
};

const getContentValue = (contentList, contentKey, fallback = '', preferredLanguages = ['cs']) => {
  if (!Array.isArray(contentList)) {
    return fallback;
  }

  const records = contentList.filter((item) => item.content_key === contentKey);
  if (!records.length) {
    return fallback;
  }

  let record = null;
  for (const language of preferredLanguages) {
    const targetLanguage = normalizeContentLanguage(language);
    record = records.find((item) => normalizeContentLanguage(item.language) === targetLanguage);
    if (record) {
      break;
    }
  }

  if (!record) {
    record = records[0];
  }

  return typeof record?.value === 'string' && record.value.trim() !== '' ? record.value : fallback;
};

function App() {
  const editryReady = isEditryConfigured();
  const locale = getCurrentLanguage();
  const preferredLanguages = getPreferredLanguages(locale);
  const { overlayEnabled, adminSession } = useEditry();
  const isEditModeActive = overlayEnabled && Boolean(adminSession?.token);
  const currentYear = new Date().getFullYear();

  usePreloadPublicData({
    contentKeys: ['footer_footer_copyright', 'footer_footer_instagram'],
    auto: editryReady,
  });
  const contentListResponse = usePublicContentList();

  const allContent = contentListResponse.data?.data || [];
  const instagramUrl = getContentValue(allContent, 'footer_footer_instagram', 'https://example.com', preferredLanguages);
  const copyrightText = getContentValue(allContent, 'footer_footer_copyright', 'loading...', preferredLanguages);

  // Get localized text
  const linksText = getLocalizedText('footer.links');
  const bikesText = getLocalizedText('footer.bikes');
  const socialText = getLocalizedText('footer.social');
  const languageText = getLocalizedText('footer.language');
  const dealersText = getLocalizedText('nav.dealers');
  const contactText = getLocalizedText('nav.contact');
  const aboutText = getLocalizedText('nav.about');
  const officialSiteText = getLocalizedText('footer.official_site');
  const prodlouzenaZaruka = getLocalizedText('footer.prodlouzena_zaruka');

  const editryLanguage = useMemo(() => {
    const LOCALE_TO_CONTENT_LANGUAGE = {
      'cs-CZ': 'cs',
      'sk-SK': 'sk',
      'pl-PL': 'pl',
      'hu-HU': 'hu',
    };
    return LOCALE_TO_CONTENT_LANGUAGE[locale] || 'cs';
  }, [locale]);

  const [categories, setCategories] = useState([]);
  const { client } = useEditry();

  useEffect(() => {
    let cancelled = false;

    const loadCategories = async () => {
      if (!editryReady) {
        setCategories([]);
        return;
      }

      try {
        const modelsResponse = await client.getPublicModels();
        const pick4Model = modelsResponse?.data?.find((model) => model.slug === 'pick-4');

        if (!pick4Model) {
          if (!cancelled) {
            setCategories([]);
          }
          return;
        }

        let itemsResponse = await client.getPublicModelItems(pick4Model.id, editryLanguage);
        let items = itemsResponse?.data || [];

        if (!items.length) {
          itemsResponse = await client.getPublicModelItems(pick4Model.id);
          items = itemsResponse?.data || [];
        }

        const parsed = items
          .map((item) => {
            const fields = getModelEntryData(item);
            return {
              cesta: fields.cesta || item.slug || '',
              nazev: fields.nazev || item.name || '',
            };
          })
          .filter((item) => item.cesta && item.nazev);

        if (!cancelled) {
          setCategories(parsed);
        }
      } catch (error) {
        if (!cancelled) {
          setCategories([]);
        }
      }
    };

    loadCategories();

    return () => {
      cancelled = true;
    };
  }, [client, editryLanguage, editryReady]);

  return (
    <div className="footer-lubau" id="footer">
      <hr className="footer-line"/>
        <div id="footer-container" className="footer-lubau-container">
          {/* 
          <div id="footer-info">
          <h1>Nukeproof</h1>
          <p>IČ: 00000000</p>
          <p>Pepa Novák</p>
          <p>Ulice 123</p>
          <p>100 00 Praha</p>
          <p><BsTelephone /> +420 123 123 123</p>
          </div>
          */}

          <div id="footer-links">
          <h1>{linksText}</h1>
          <NavLink to="/prodejci">{dealersText}</NavLink>
          <NavLink to="/kontakt">{contactText}</NavLink>
          <NavLink to="/o-nukeproof">{aboutText}</NavLink>
          <NavLink to="https://www.nukeproof.com/en_CZ" target='_blank' rel="noopener noreferrer">{officialSiteText}</NavLink>
          <NavLink to="/prodlouzena-zaruka">{prodlouzenaZaruka}</NavLink>
          </div>

          <div id="footer-links">
          <h1>{bikesText}</h1>
          {categories.map((category) => (
            <NavLink key={category.cesta} to={`/kola/${category.cesta}`}>
              {category.nazev}
            </NavLink>
          ))}
          </div>

          <div id="footer-social">
          <h1>{socialText}</h1>
            <div className="footer-social-container">
              <a href={instagramUrl || "https://example.com"} target='_blank' rel="noopener noreferrer"><FaInstagram /></a>
            </div>
            {isEditModeActive && (
              <div style={{ marginTop: '8px' }}>
                <EditableContent
                  contentKey="footer_footer_instagram"
                  as="span"
                  fallback={instagramUrl}
                  showFallbackWhileLoading={false}
                />
              </div>
            )}
          </div>

          <div id="footer-language">
          <h1>{languageText}</h1>
            <LanguageSwitcher />
          </div>
        </div>

        {/* 
        <div className="footer-switch">
          <label className="switch">
          <input type="checkbox" onChange={() => changeTheme()} defaultChecked={mode === "light"} />
          <span className="slider round">
            <span className="icon sun"><IoIosMoon /></span>
            <span className="icon moon"><FaSun /></span>
          </span>
          </label>
        </div>
        */}
        
        <h2>
          Copyright © {currentYear} {isEditModeActive ? (
            <EditableContent
              contentKey="footer_footer_copyright"
              as="span"
              fallback={copyrightText}
              showFallbackWhileLoading={false}
            />
          ) : (
            copyrightText
          )}
        </h2>
        <h3>Vyrobila mediální skupina <NavLink to="https://lazylabs.cz/">LazyLabs.cz</NavLink></h3>
    </div>
  );
}

export default App;
